import type { DocumentSectionType } from '@alpod-ai-writer/shared';
import { useCallback, useEffect, useState, useTransition } from 'react';

import { useNavBlocker } from '../../hooks/useNavBlocker.hook';
import type { EditorSection } from '../types/editorSection.types';

export interface SectionAnchor {
    sectionId: string;
    headingId?: string;
}

function getSectionAnchorFromHash(sections: EditorSection[]): SectionAnchor|null {
    const hash = window.location.hash.slice(1);
    const parts = hash.split('/').map(decodeURIComponent);
    const section = sections.find((section) => section.id === parts[0]);
    return {
        sectionId: section?.id ?? sections[0]?.id ?? null,
        headingId: parts[1]
    };
}

export function getSectionLink(sectionId: string, headingId?: string) {
    return '#' + encodeURIComponent(sectionId) + (headingId ? '/' + encodeURIComponent(headingId) : '');
}

export function useSectionNav(sections: EditorSection[]) {
    const [ currentSection, setSection ] = useState<SectionAnchor|null>(getSectionAnchorFromHash(sections));
    const [ isPending, startTransition ] = useTransition();
    const { blockers } = useNavBlocker();

    const selectSection = useCallback((anchor: SectionAnchor) => {
        if (blockers.has('documentSettings')) {
            const res = confirm('Máte neuložené změny. Opravdu chcete odejít z této stránky?');

            if (!res) {
                return;
            }
        }

        window.location.hash = getSectionLink(anchor.sectionId, anchor.headingId);
    }, [ setSection, blockers ]);

    const selectSectionByType = useCallback((type: DocumentSectionType, heading?: string) => {
        const firstSectionOfType = sections.find((section) => section.type === type);

        if (firstSectionOfType) {
            selectSection({ sectionId: firstSectionOfType.id, headingId: heading });
        }
    }, [ sections, setSection ]);

    const handleHashChange = useCallback(() => {
        const newAnchor = getSectionAnchorFromHash(sections);
        startTransition(() => {
            setSection(newAnchor);
        });
    }, [ setSection, sections, startTransition ]);

    useEffect(() => {
        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [ handleHashChange ]);

    return {
        currentSection,
        isPending,
        selectSection,
        selectSectionByType
    };
}
